/**
 * Ops usage contracts.
 *
 * Data sources:
 *   - gateway `usage_logs` owns request/token/latency facts.
 *   - platform `revenue_ledger` owns financial truth and margin.
 *
 * These shapes are read-only operator surfaces. Tenant-facing `/v1/usage*`
 * contracts stay in `public-usage.ts`.
 *
 * ─── Canonical success-rate definition (epic #997 / #998) ─────────────
 *
 * This is the single written definition every business metric derives
 * from. Producers that deviate must label the alternate definition in
 * their own contract doc.
 *
 *   - Row population: `usage_logs` rows with `is_probe = 0` and the
 *     retry-attempt exclusion applied (attempt rows carry the parent
 *     request id plus an `_a<N>` suffix; only the terminal request row
 *     counts). Business aggregates also never include `acc_system`
 *     spend (probes/canaries, Architecture Rule 6 / ADR-003).
 *   - success  := `200 <= status_code < 400`  (`successCount`)
 *   - failure  := anything else, i.e. `NOT success` (router-stats
 *     `failures`); `errorCount` below is the `status_code >= 400`
 *     subset — identical to `requestCount - successCount` for the HTTP
 *     statuses the gateway writes today.
 *   - success rate := `successCount / requestCount` (0 when
 *     `requestCount = 0` — never NaN on the wire).
 *
 * Consumers of this definition: the ops usage aggregate
 * (gateway `internal-usage.ts`) and the router-stats trend
 * (gateway `internal-router-stats.ts`) — for the same window they
 * produce identical day totals.
 *
 * Relationship to the unhealthy-upstream triage list
 * (`internal-dashboard-health.ts`): that surface deliberately uses a
 * NARROWER failure notion — `status_code >= 500` over non-probe 24h
 * traffic, gated by minimum-request and failure-rate thresholds. It is
 * an alerting heuristic ("is this upstream broken?"), not a business
 * success rate: 4xx responses (tenant errors, rate limits) count
 * against the canonical success rate but do not indict an upstream.
 * The two numbers are expected to differ on the same screen; render
 * them with their own labels.
 */

import type { BrandId } from './brands.ts';
import type { UsageRatingStatus } from './ops-support.ts';
import { isRecord } from './validation.ts';

export type OpsUsageStatusFilter = 'success' | 'error';

export interface InternalOpsUsageTotals {
  readonly requestCount: number;
  readonly successCount: number;
  readonly errorCount: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  /** Gateway `usage_logs.cost_usd_cents`, compatibility integer USD cents. */
  readonly costUsdCents: number;
  /** Gateway `usage_logs.cost_usd_micros`, authoritative integer micro-USD. */
  readonly costUsdMicros?: number;
}

export interface InternalOpsUsageByTenantRow extends InternalOpsUsageTotals {
  readonly accountId: string;
  readonly orgId: string;
}

export interface InternalOpsUsageByModelRow extends InternalOpsUsageTotals {
  readonly vendor: string;
  readonly model: string;
}

export interface InternalOpsUsageByUpstreamRow extends InternalOpsUsageTotals {
  readonly upstreamId: string;
}

export interface InternalOpsUsageByKeyRow extends InternalOpsUsageTotals {
  readonly apiKeyId: string;
  readonly accountId: string;
  readonly orgId: string;
}

export interface InternalOpsUsageByBrandRow extends InternalOpsUsageTotals {
  readonly brand: BrandId;
}

/**
 * By-SKU aggregate rows (epic #1035 E6-D4, #1068). Grouped on the #902
 * usage snapshot columns; both fields are null for legacy rows written
 * before the SKU cutover, which aggregate into a single null bucket.
 */
export interface InternalOpsUsageBySkuRow extends InternalOpsUsageTotals {
  readonly skuId: string | null;
  readonly skuName: string | null;
}

/**
 * POST body for `/internal/usage/ops/requests` and `/internal/usage/ops/aggregate`.
 *
 * `orgIds` travels in the JSON body so a large customer-manager book does
 * not overflow Node's 16 KB request-header limit (#2564). A body has no
 * URL limit; 5000 ids × ~12 B is ~60 KB, which is fine. GET still accepts
 * the same list as a comma-separated query param for small books.
 */
export interface InternalOpsUsageOrgIdsBody {
  readonly orgIds: readonly string[];
  readonly from?: string;
  readonly to?: string;
  readonly accountId?: string;
  readonly orgId?: string;
  readonly limit?: number | string;
  readonly offset?: number | string;
  readonly brand?: string;
  readonly status?: string;
  readonly requestId?: string;
  readonly detail?: string;
}

export interface InternalOpsUsageAggregateResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly totals: InternalOpsUsageTotals;
  readonly tenants: readonly InternalOpsUsageByTenantRow[];
  readonly models: readonly InternalOpsUsageByModelRow[];
  readonly upstreams: readonly InternalOpsUsageByUpstreamRow[];
  readonly keys: readonly InternalOpsUsageByKeyRow[];
  readonly brands?: readonly InternalOpsUsageByBrandRow[];
  /** Optional while gateway deploys roll: absent from older responses. */
  readonly skus?: readonly InternalOpsUsageBySkuRow[];
  readonly upstreamNames: Record<string, string>;
}

export interface OpsUsageTotals extends InternalOpsUsageTotals {
  /** Platform `revenue_ledger.usd_charged_micros`, authoritative integer micro-USD. */
  readonly usdChargedMicros?: number;
  /** Platform `revenue_ledger.usd_charged_cents`, integer USD cents. */
  readonly usdChargedCents: number;
  readonly cnyUpstreamCostCents: number;
  readonly l2ProfitCnyCents: number;
  readonly l2MarginPct: number;
  readonly totalGrossMarginCnyCents: number;
}

export interface OpsUsageByTenantRow extends InternalOpsUsageByTenantRow {
  readonly email: string;
  readonly usageUsdChargedMicros?: number;
  readonly usageUsdChargedCents: number;
  readonly l2ProfitCnyCents: number;
  readonly totalCnyCents: number;
}

export interface OpsUsageByModelRow extends InternalOpsUsageByModelRow {
  readonly usageUsdChargedMicros?: number;
  readonly usageUsdChargedCents: number;
  readonly cnyUpstreamCostCents: number;
  readonly l2ProfitCnyCents: number;
  readonly l2MarginPct: number;
}

export interface OpsUsageByUpstreamRow extends InternalOpsUsageByUpstreamRow {
  readonly upstreamName: string;
}

export interface OpsUsageByKeyRow extends InternalOpsUsageByKeyRow {
  readonly apiKeyName: string | null;
  readonly apiKeyPrefix: string | null;
  readonly apiKeyRevokedAt: string | null;
  readonly channelId: string | null;
  readonly channelSlug: string | null;
  readonly channelName: string | null;
}

export interface OpsUsageByBrandRow extends OpsUsageTotals {
  readonly brand: BrandId;
}

export interface OpsUsageOverviewResponse {
  readonly window: {
    readonly from: string;
    readonly to: string;
  };
  readonly totals: OpsUsageTotals;
  readonly tenants: readonly OpsUsageByTenantRow[];
  readonly models: readonly OpsUsageByModelRow[];
  readonly upstreams: readonly OpsUsageByUpstreamRow[];
  readonly keys: readonly OpsUsageByKeyRow[];
  readonly brands?: readonly OpsUsageByBrandRow[];
}

export interface OpsUsageRequestRow {
  readonly id: string;
  readonly requestId: string;
  readonly accountId: string;
  readonly accountEmail: string;
  readonly orgId: string;
  readonly apiKeyId: string;
  readonly ingressBrand?: BrandId | null;
  readonly ingressHost?: string | null;
  readonly apiKeyBrand?: BrandId | null;
  readonly apiKeyName: string | null;
  readonly apiKeyPrefix: string | null;
  readonly apiKeyRevokedAt: string | null;
  readonly channelId: string | null;
  readonly channelSlug: string | null;
  readonly channelName: string | null;
  /** Epic #898 P6 (#914): SKU snapshot; null on legacy rows. */
  readonly skuId?: string | null;
  readonly skuName?: string | null;
  readonly vendor: string;
  readonly model: string;
  readonly protocol: string;
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly upstreamAccountId: string | null;
  readonly upstreamKeyBucketId: string | null;
  readonly upstreamChannelId: string | null;
  readonly upstreamChannelSlug: string | null;
  readonly routeResolutionSource: string | null;
  readonly egressProfileId: string | null;
  /**
   * Egress lane for the request (typed model, epic protocol#473). Sourced
   * from the frozen `usage_logs.egress_type` column relay: historic rows
   * carry legacy dialect values, post-R1 rows carry `EgressLane` values.
   */
  readonly lane: string | null;
  readonly stream: boolean | null;
  readonly lifecycleStatus: string | null;
  readonly upstreamAttempts: number | null;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  /** Gateway `usage_logs.cost_usd_cents`, compatibility integer USD cents. */
  readonly costUsdCents: number;
  /** Gateway `usage_logs.cost_usd_micros`, authoritative integer micro-USD. */
  readonly costUsdMicros?: number;
  /**
   * Whether the money above is final (#1410 P3). Absent means `'rated'` — see
   * `UsageRatingStatus`. An operator page that renders a confident zero for a
   * `'pending'` row tells the person handling the billing question the request
   * was free, minutes before the charge lands.
   */
  readonly ratingStatus?: UsageRatingStatus;
  readonly statusCode: number;
  readonly latencyMs: number | null;
  readonly clientResponseElapsedMs: number | null;
  readonly createdAt: string;
  readonly traceAvailable?: boolean;
  /**
   * Present only when this row was served from `usage_logs_archive`
   * (`true`). Omitted on live rows; ops-api relays it untouched.
   */
  readonly archived?: true;
  /**
   * Present only on a request the cc-lane admission blacklist intercepted
   * (router#1609); absent on every other row.
   *
   * Two named fields rather than the `usage_details_json` blob they come from.
   * That column also carries `audit`, `segments` and `raw_usage`, and widening
   * an operator projection to "whatever is in the JSON column" puts fields on
   * an operator screen that nobody reviewed for that audience — and keeps
   * doing so every time something new is added to `UsageDetails`.
   *
   * Without this the refusal is visible in the request list (status code, zero
   * tokens, `zero_cost`) but *which rule fired and what it did* is not, which
   * is the question an operator who armed the switch actually has.
   */
  readonly admission?: OpsUsageRequestAdmission;
}

/**
 * The blacklist verdict recorded on an intercepted request (router#1609), and
 * since #1689 the client identity it was decided from.
 *
 * The identity fields are optional because rows written before #1689 carry
 * only the verdict; absence means "historic row", not "unclassified client".
 */
export interface OpsUsageRequestAdmission {
  /** Rule key that fired, e.g. `bareApiOnCcLane`. */
  readonly blacklistRule: string;
  /** Action applied, e.g. `model-unavailable`. */
  readonly blacklistAction: string;
  /** Client class the rule matched on, e.g. `anthropic-api`. */
  readonly clientKind?: string;
  /** cc entrypoint the client declared, e.g. `sdk-cli`; absent when none. */
  readonly clientEntrypoint?: string;
  /** Egress lane the request had reached, e.g. `claude-code`. */
  readonly lane?: string;
}

/**
 * The blacklist verdict and the identity it was decided from, projected field
 * by field out of a `usage_details_json` string (router#1609, widened by
 * #1689, shared here by #1615).
 *
 * Deliberately NOT "relay the column". `usage_details_json` also carries
 * `audit`, `segments` and `raw_usage`; passing the blob through would put
 * whatever `UsageDetails` grows next onto an operator screen without anyone
 * deciding it belonged there. The field list is the surface, and every entry
 * on it is here because somebody decided it belonged.
 *
 * It lives in contracts rather than in one reader because two operator
 * surfaces need the same answer from opposite sides of ops-api. The
 * `/v1/ops/usage/requests` list projects `admission` onto its row here; the
 * `/v1/ops/support/requests` row spreads the gateway entry and reaches the
 * console still carrying the raw column, so the console parses it there. One
 * copy of the field list means the two surfaces cannot drift into disagreeing
 * about what an interception is.
 *
 * The verdict pair is required, the identity is not: rows written before #1689
 * carry no identity, and they must still decorate rather than vanish.
 *
 * Unparseable or absent means absent: this decorates a request list, so a
 * malformed detail blob on one historic row must not fail the page. The row
 * still renders with everything else it carries.
 */
export function parseUsageDetailsAdmission(
  raw: string | null | undefined,
): OpsUsageRequestAdmission | null {
  if (raw === null || raw === undefined || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const admission = parsed['admission'];
  if (!isRecord(admission)) return null;
  const rule = admission['blacklist_rule'];
  const action = admission['blacklist_action'];
  if (typeof rule !== 'string' || typeof action !== 'string') return null;
  const clientKind = admission['client_kind'];
  const clientEntrypoint = admission['client_entrypoint'];
  const lane = admission['lane'];
  return {
    blacklistRule: rule,
    blacklistAction: action,
    ...(typeof clientKind === 'string' ? { clientKind } : {}),
    ...(typeof clientEntrypoint === 'string' ? { clientEntrypoint } : {}),
    ...(typeof lane === 'string' ? { lane } : {}),
  };
}

export interface OpsUsageRequestsResponse {
  readonly rows: readonly OpsUsageRequestRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly errors?: readonly string[];
}
